import { useEffect, useState, useCallback } from 'react';
import { 
  PerformanceMonitor,
  BudgetType,
  MetricType,
  initializePerformanceMonitor,
  getPerformanceMonitor,
  PERFORMANCE_BUDGETS
} from '@/utils/performanceBudgets';
import { useConnectionMode } from '@/connection/SimpleConnectionProvider';

interface BudgetStatus {
  metric: MetricType;
  actual: number;
  budget: number;
  percentage: number;
  exceeded: boolean;
  warning: boolean; // > 80% of budget
}

interface PerformanceBudgetHook {
  budgetStatuses: BudgetStatus[];
  performanceScore: number;
  violations: BudgetStatus[];
  warnings: BudgetStatus[];
  isMonitoring: boolean;
  refresh: () => void;
  budgetType: BudgetType;
  overallStatus: 'good' | 'warning' | 'critical';
}

export const usePerformanceBudget = (
  customBudgetType?: BudgetType,
  autoStart = true
): PerformanceBudgetHook => {
  const [monitor, setMonitor] = useState<PerformanceMonitor | null>(null);
  const [budgetStatuses, setBudgetStatuses] = useState<BudgetStatus[]>([]);
  const [performanceScore, setPerformanceScore] = useState(100);
  const [isMonitoring, setIsMonitoring] = useState(false);
  
  const connectionMode = useConnectionMode();
  const budgetType: BudgetType = customBudgetType || (connectionMode === 'lite' ? 'lite' : 'full');

  const calculateBudgetStatuses = useCallback((monitor: PerformanceMonitor): BudgetStatus[] => {
    const results = monitor.checkBudgets();
    return results.map(result => ({
      metric: result.metric,
      actual: result.actual,
      budget: result.budget,
      percentage: (result.actual / result.budget) * 100,
      exceeded: result.over,
      warning: !result.over && result.actual > result.budget * 0.8
    }));
  }, []);

  const refresh = useCallback(() => {
    const currentMonitor = getPerformanceMonitor();
    if (currentMonitor) {
      const statuses = calculateBudgetStatuses(currentMonitor);
      const score = currentMonitor.getPerformanceScore();
      
      setBudgetStatuses(statuses);
      setPerformanceScore(score);
    }
  }, [calculateBudgetStatuses]);

  const startMonitoring = useCallback(() => {
    // Clean up existing monitor
    if (monitor) {
      monitor.destroy();
    }

    const newMonitor = initializePerformanceMonitor(budgetType);
    setMonitor(newMonitor);
    setIsMonitoring(true);

    // Initial measurement after a brief delay
    setTimeout(() => {
      newMonitor.measureResourceSizes();
      refresh();
    }, 1000);

    // Set up periodic updates
    const interval = setInterval(() => {
      const statuses = calculateBudgetStatuses(newMonitor);
      const score = newMonitor.getPerformanceScore();
      
      setBudgetStatuses(statuses);
      setPerformanceScore(score);
    }, 3000);

    // Stop monitoring after 30 seconds to prevent memory leaks
    setTimeout(() => {
      clearInterval(interval);
      setIsMonitoring(false);
    }, 30000);

    return () => {
      clearInterval(interval);
      newMonitor.destroy();
    };
  }, [budgetType, monitor, calculateBudgetStatuses, refresh]);

  useEffect(() => {
    if (autoStart && typeof window !== 'undefined') {
      // Start monitoring after page load
      if (document.readyState === 'complete') {
        startMonitoring();
      } else {
        window.addEventListener('load', startMonitoring);
        return () => window.removeEventListener('load', startMonitoring);
      }
    }
  }, [autoStart, startMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (monitor) {
        monitor.destroy();
      }
    };
  }, [monitor]);

  // Derived values
  const violations = budgetStatuses.filter(status => status.exceeded);
  const warnings = budgetStatuses.filter(status => status.warning && !status.exceeded);
  
  const overallStatus: 'good' | 'warning' | 'critical' = 
    violations.length > 0 ? 'critical' :
    warnings.length > 0 ? 'warning' : 'good';

  return {
    budgetStatuses,
    performanceScore,
    violations,
    warnings,
    isMonitoring,
    refresh,
    budgetType,
    overallStatus
  };
};

// Hook for specific metric monitoring
export const useMetricBudget = (
  metric: MetricType,
  customBudgetType?: BudgetType
) => {
  const { budgetStatuses, budgetType } = usePerformanceBudget(customBudgetType);
  
  const metricStatus = budgetStatuses.find(status => status.metric === metric);
  const budget = PERFORMANCE_BUDGETS[budgetType][metric];
  
  return {
    actual: metricStatus?.actual || 0,
    budget,
    percentage: metricStatus?.percentage || 0,
    exceeded: metricStatus?.exceeded || false,
    warning: metricStatus?.warning || false,
    budgetType
  };
};

// Hook for early warning system
export const useBudgetAlerts = (
  onViolation?: (metric: MetricType, status: BudgetStatus) => void,
  onWarning?: (metric: MetricType, status: BudgetStatus) => void
) => {
  const { violations, warnings } = usePerformanceBudget();
  
  useEffect(() => {
    violations.forEach(violation => {
      onViolation?.(violation.metric, violation);
    });
  }, [violations, onViolation]);
  
  useEffect(() => {
    warnings.forEach(warning => {
      onWarning?.(warning.metric, warning);
    });
  }, [warnings, onWarning]);
  
  return { violations, warnings };
};